/**
 * The public site's page tree, for the copy directory. Every page shows up in
 * the directory; pages that have copy *variants* (see COPY_PAGES in
 * variants.ts) get the variant picker, the rest show their current copy
 * read-only. `key` is the /office/copy/<key> route segment; `label` is the
 * directory display (site path); `namespace` is the locale namespace whose
 * strings the page renders.
 */
export type SitePage = {
  key: string;
  label: string;
  namespace: string;
  children?: SitePage[];
};

export const SITE_PAGES: SitePage[] = [
  {
    key: "home",
    label: "[index]",
    namespace: "home",
    children: [
      { key: "services", label: "services", namespace: "services" },
      { key: "podcasts", label: "podcasts", namespace: "podcasts" },
      {
        key: "rentals",
        label: "rentals",
        namespace: "rentals",
        children: [
          {
            key: "rentals-catalog",
            label: "catalog",
            namespace: "rentalsCatalog",
          },
          { key: "rentals-item", label: "[item]", namespace: "rentalsItem" },
        ],
      },
      {
        key: "cinematography",
        label: "cinematography",
        namespace: "cinematography",
      },
      { key: "projects", label: "projects", namespace: "projects" },
      { key: "stills", label: "stills", namespace: "stills" },
      { key: "team", label: "team", namespace: "team" },
      { key: "faqs", label: "faqs", namespace: "faqs" },
      { key: "contact", label: "contact", namespace: "contact" },
      { key: "privacy", label: "privacy", namespace: "privacy" },
    ],
  },
];

export type FlatSitePage = {
  key: string;
  label: string;
  namespace: string;
  /** 0 = root (`[index]`); descendants increase by one. */
  depth: number;
  /** Whether this node is the last among its siblings (controls └ vs ├). */
  isLast: boolean;
  /** For each ancestor level, whether that ancestor was the last child — i.e.
   *  whether a continuation vertical should be drawn at that column. The tree
   *  lines are rendered with CSS hairlines (not box-drawing glyphs, which fall
   *  back to a non-monospace font and misalign). */
  ancestorsLast: boolean[];
};

/** Depth-first flatten with the structure needed to draw CSS tree lines. The
 *  root (`[index]`) is drawn with no connector; its descendants nest under it. */
export function flattenSiteTree(
  pages: SitePage[] = SITE_PAGES,
): FlatSitePage[] {
  const out: FlatSitePage[] = [];
  const walk = (
    nodes: SitePage[],
    ancestorsLast: boolean[],
    isRoot: boolean,
  ) => {
    nodes.forEach((node, i) => {
      const isLast = i === nodes.length - 1;
      out.push({
        key: node.key,
        label: node.label,
        namespace: node.namespace,
        depth: isRoot ? 0 : ancestorsLast.length + 1,
        isLast: isRoot ? true : isLast,
        ancestorsLast,
      });
      if (node.children) {
        walk(node.children, isRoot ? [] : [...ancestorsLast, isLast], false);
      }
    });
  };
  walk(pages, [], true);
  return out;
}

export function findSitePage(
  key: string,
  pages: SitePage[] = SITE_PAGES,
): SitePage | undefined {
  for (const p of pages) {
    if (p.key === key) return p;
    if (p.children) {
      const found = findSitePage(key, p.children);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Breadcrumb path (verbatim label + route key, top page → target) for a
 * `/office/copy/<key>` page. The tree root (`[index]`) is dropped unless it's
 * the target itself, so a nested page reads `rentals / [item]` rather than
 * `[index] / rentals / [item]`. Empty if the key isn't in the tree.
 */
export function copyCrumbPath(key: string): { label: string; key: string }[] {
  const path: SitePage[] = [];
  const walk = (nodes: SitePage[]): boolean => {
    for (const n of nodes) {
      path.push(n);
      if (n.key === key) return true;
      if (n.children && walk(n.children)) return true;
      path.pop();
    }
    return false;
  };
  if (!walk(SITE_PAGES)) return [];
  const trimmed = path.length > 1 ? path.slice(1) : path;
  return trimmed.map((n) => ({ label: n.label, key: n.key }));
}
